// ── Root ──────────────────────────────────────────────────────────────────────
//
// pervend.com. Two pages: the landing page, and the card fee calculator —
// a free lead tool with no sign-up.
//
// ROUTING. There is no router and no backend. The host rewrites every unknown
// path to index.html, and this branch picks the page from the pathname, the
// same way Data IQ does. An unrecognised path falls back to the landing page
// rather than dead-ending.
//
// METADATA. src/routes.json is the single source of truth for both the route
// table here AND the per-route <meta> written at build time by
// scripts/prerender-meta.js. That script exists because link-preview unfurlers
// on Slack, LinkedIn and Facebook read served HTML and never execute
// JavaScript — so a page whose title is set by React previews as whatever the
// shared index.html happened to say. This page gets shared in trade forums, so
// it has to be right. Add a route in routes.json, add it to PAGES below, add
// its rewrite to render.yaml, and the build writes its HTML and sitemap entry
// — and fails if the render.yaml rewrite is missing.

import React from "react";
import brandJson from "./brand.json";
import { ROUTES } from "./routes";
import LandingPage from "./LandingPage";
import CardFeeCalculator from "./CardFeeCalculator";
import NotesIndex from "./NotesIndex";
import Note from "./Note";
import type { Brand, PageProps } from "./types";

// Brand, not theme. These do not vary with light/dark — the same teal carries
// on both grounds — so they live in src/brand.json rather than in theme.css.
// JSON for the same reason as routes.json: scripts/prerender-meta.js reads it
// too, and checks that the contact details in public/index.html's <noscript>
// still match — so a changed phone number cannot leave a stale one behind.
//
// Teal specifically because the semantic tokens already spend green on
// "success", red on "danger" and amber on "warning". An accent that collides
// with a status colour stops reading as an accent. It also has to stay light
// enough for the black --on-accent text that sits on it (~6.5:1 here).
//
// The mailbox has to exist before this page is public — it is half of the
// only call to action on the site.
export const BRAND: Brand = Object.freeze({ ...brandJson });

const PAGES: Readonly<Record<string, React.ComponentType<PageProps>>> = {
  LandingPage,
  CardFeeCalculator,
  NotesIndex,
  Note,
};

/** Trailing slashes and case are not meaningful here, and "/" must survive the strip. */
function normalise(pathname: string): string {
  return pathname.replace(/\/+$/, "").toLowerCase() || "/";
}

function pageFor(path: string): React.ComponentType<PageProps> {
  const route = ROUTES[path] ?? ROUTES["/"];
  return PAGES[route.component] ?? LandingPage;
}

/**
 * /Card-Fees and /card-fees/ render the calculator, but the host may have served
 * them the landing page's document — only the exact path in routes.json has a
 * rewrite to its prerendered file (render.yaml). Put that spelling in the
 * address bar so a link copied from it previews right.
 * Unknown paths are left alone: they fall back to the landing page by design.
 */
function canonicalise(): string {
  const { pathname, search, hash } = window.location;
  const path = normalise(pathname);
  if (ROUTES[path] && pathname !== path) {
    window.history.replaceState(null, "", `${path}${search}${hash}`);
  }
  return path;
}

export default function App(): React.JSX.Element {
  const path = canonicalise();
  const Page = pageFor(path);
  return (
    <>
      <style>{`
        *{box-sizing:border-box;margin:0;padding:0;}
        html,body,#root{background:var(--bg);color:var(--text);min-height:100vh;font-family:'Barlow',sans-serif;}
        html{scroll-behavior:smooth;}
        ::-webkit-scrollbar{width:8px;height:8px;}
        ::-webkit-scrollbar-track{background:var(--surface);}
        ::-webkit-scrollbar-thumb{background:var(--border-strong);border-radius:4px;}
        ::-webkit-scrollbar-thumb:hover{background:var(--text-faint);}
        input::placeholder{color:var(--text-muted);}
        a:focus-visible,input:focus-visible,button:focus-visible{outline:2px solid ${BRAND.primary};outline-offset:2px;}
        @media (prefers-reduced-motion: reduce){html{scroll-behavior:auto;}}
      `}</style>
      <Page brand={BRAND} path={path} />
    </>
  );
}
